import type { Availability, ManyOfManyPoll, Persisted, PollResponse } from '@tunasong/schemas'
import { isDateSlot } from '@tunasong/schemas'
import { Typography, UserAvatar, useDimensions } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { formatWaitingOn, getSlotTally, getWaitingOn } from './availability.js'
import { formatSlotFullLabel, formatSlotLabel, getLiveSlotIndices } from './date-slot.js'
import styles from './responses-matrix.module.css'

export interface ResponsesMatrixProps {
  poll: Persisted<ManyOfManyPoll>
  responses: Persisted<PollResponse>[]
  memberIds: string[]
  /** userId → display name, for the waiting-on line. Falls back to the userId. */
  memberNames?: Record<string, string>
  /** Injected in tests so "past" is deterministic. */
  now?: string
}

const stateLabels: Record<Availability, string> = { yes: 'Yes', maybe: 'Maybe', no: 'No' }
const noAnswer = 'No answer'

const answerFor = (response: Persisted<PollResponse>, idx: number) => {
  const value = response.availability?.[String(idx)]
  return value ? stateLabels[value] : noAnswer
}

/**
 * The respondent × slot grid that was rejected for voting, in the layout where it belongs:
 * reading, not deciding. This is the only breakpoint-sensitive surface in the feature.
 *
 * It also loads once. The previous Responses view rendered one poll component per respondent,
 * each re-running the same children query.
 */
export const ResponsesMatrix: FC<ResponsesMatrixProps> = props => {
  const { poll, responses, memberIds, memberNames, now } = props

  const { isMediumOrSmaller } = useDimensions()
  const liveIndices = getLiveSlotIndices(poll)
  const waitingOn = formatWaitingOn(getWaitingOn(poll, memberIds, responses, now).map(id => memberNames?.[id] ?? id))

  if (responses.length === 0) {
    return (
      <Typography className={styles.empty} color="secondary">
        No responses yet
      </Typography>
    )
  }

  const waiting = waitingOn ? (
    <Typography variant="caption" className={styles.waiting}>
      {waitingOn.visible === waitingOn.full ? (
        <span data-waiting-full>{waitingOn.full}</span>
      ) : (
        <>
          <span aria-hidden="true">{waitingOn.visible}</span>
          <span data-waiting-full className={styles.visuallyHidden}>
            {waitingOn.full}
          </span>
        </>
      )}
    </Typography>
  ) : null

  if (isMediumOrSmaller) {
    return (
      <div className={styles.cards}>
        {responses.map(response => (
          <div key={response.id} data-testid="respondent-card" className={styles.card}>
            <UserAvatar userId={response.userId} className={styles.avatar} />
            <dl>
              {liveIndices.map(idx => {
                const slot = poll.pollOptions[idx]
                return isDateSlot(slot) ? (
                  <div key={idx}>
                    <dt>{formatSlotLabel(slot.start)}</dt>
                    <dd>{answerFor(response, idx)}</dd>
                  </div>
                ) : null
              })}
            </dl>
          </div>
        ))}
        {waiting}
      </div>
    )
  }

  return (
    <div className={styles.container}>
      {/* Wide polls scroll inside this container so the page body never scrolls sideways. */}
      <div data-matrix-scroll className={styles.scroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col">Respondent</th>
              {liveIndices.map(idx => {
                const slot = poll.pollOptions[idx]
                return isDateSlot(slot) ? (
                  <th key={idx} scope="col" title={formatSlotFullLabel(slot)}>
                    {formatSlotLabel(slot.start)}
                  </th>
                ) : null
              })}
            </tr>
          </thead>
          <tbody>
            {responses.map(response => (
              <tr key={response.id}>
                <th scope="row">
                  <UserAvatar userId={response.userId} className={styles.avatar} />
                </th>
                {liveIndices.map(idx => (
                  <td key={idx}>{answerFor(response, idx)}</td>
                ))}
              </tr>
            ))}
            <tr>
              <th scope="row">Available</th>
              {liveIndices.map(idx => (
                <td key={idx}>{`${getSlotTally(responses, idx).yes} of ${memberIds.length} available`}</td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      {waiting}
    </div>
  )
}

export default ResponsesMatrix
